import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useApi from '../../api';
import { Button } from '@/components/ui/button';
import { Undo2 } from 'lucide-react';
import { formatDate } from '../../utils/dateFormatter';
import { ServiceVersion, apiErrorMessage, describeTrigger } from '../servicesShared';
import { SectionHeader } from './editorShared';

/** Published version history (immutable) with restore-into-draft. */
export const VersionsTab: React.FC<{
    serviceId: string;
    currentVersionId?: string | null;
    hasDraft: boolean;
    onRestore: (version: ServiceVersion) => Promise<void>;
}> = ({ serviceId, currentVersionId, hasDraft, onRestore }) => {
    const { get } = useApi();
    const [versions, setVersions] = useState<ServiceVersion[]>([]);
    const [loading, setLoading] = useState(true);
    const [restoring, setRestoring] = useState<string | null>(null);
    const [openId, setOpenId] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        get(`/services/${serviceId}/versions`)
            .then((response) => {
                if (!cancelled) setVersions(response.data.data || []);
            })
            .catch((error) => toast.error(apiErrorMessage(error, 'Failed to load versions')))
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceId, currentVersionId]);

    return (
        <section className="mb-10">
            <SectionHeader
                title="Versions"
                description="Published versions are immutable; every job follows the latest at its next wake. Restoring copies one into the draft — publishing it creates a new version number."
            />
            <div className="rounded-lg border divide-y">
                {loading && <p className="p-3 text-sm text-muted-foreground">Loading…</p>}
                {!loading && !versions.length && <p className="p-3 text-sm text-muted-foreground">Nothing published yet.</p>}
                {versions.map((version) => {
                    const open = openId === version.id;
                    return (
                        <div key={version.id} className="px-3 py-2">
                            <div className="flex items-center justify-between gap-3">
                                <button type="button" className="min-w-0 text-left" onClick={() => setOpenId(open ? null : version.id)}>
                                    <div className="text-sm font-medium">
                                        v{version.version_number}
                                        {version.id === currentVersionId && <span className="ml-2 text-xs text-green-700 dark:text-green-400">current</span>}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">
                                        {version.published_at ? formatDate(version.published_at) : '—'} · {version.completion === 'ai' ? 'bounded' : 'standing'} ·{' '}
                                        {version.routines?.length || 0} routine{version.routines?.length === 1 ? '' : 's'} ·{' '}
                                        {version.instructions.slice(0, 80) || 'No instructions'}
                                    </div>
                                </button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={restoring === version.id}
                                    title={hasDraft ? 'Replaces the current draft' : undefined}
                                    onClick={async () => {
                                        setRestoring(version.id);
                                        try {
                                            await onRestore(version);
                                        } finally {
                                            setRestoring(null);
                                        }
                                    }}
                                >
                                    <Undo2 className="h-3.5 w-3.5 mr-1" />
                                    Restore
                                </Button>
                            </div>
                            {open && (
                                <div className="mt-2 grid gap-3 text-xs sm:grid-cols-2">
                                    <div>
                                        <div className="text-muted-foreground mb-1">Instructions</div>
                                        <pre className="max-h-48 overflow-auto rounded bg-muted p-2 whitespace-pre-wrap">{version.instructions || '—'}</pre>
                                    </div>
                                    <div className="space-y-2">
                                        <div>
                                            <div className="text-muted-foreground mb-1">Guardrails</div>
                                            <pre className="max-h-24 overflow-auto rounded bg-muted p-2 whitespace-pre-wrap">{version.guardrails || '—'}</pre>
                                        </div>
                                        <div>
                                            <div className="text-muted-foreground mb-1">Routines</div>
                                            {version.routines?.length ? (
                                                <ul className="list-disc pl-4">
                                                    {version.routines.map((routine) => (
                                                        <li key={routine.name}>
                                                            <span className="font-mono">{routine.name}</span> · {routine.kind} · {describeTrigger(routine.trigger)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : (
                                                '—'
                                            )}
                                        </div>
                                        <div>
                                            <div className="text-muted-foreground mb-1">State</div>
                                            {Object.keys(version.state_schema?.fields || {}).join(', ') || '—'}
                                            {version.state_schema?.headline ? ` → "${version.state_schema.headline}"` : ''}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

export default VersionsTab;
